import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, EventCard, EventFormModal, LoadingSpinner } from '../components'
import { API_BASE_URL, EventCardMeta, EventFormModalMeta, LoadingSpinnerMeta } from '../data'
import { Event, EventsMetaProps } from '../data/types'
import { PlusIcon, ChevronLeftIcon } from '../icons'
import { useResourcePermissions } from '../hooks/useResourcePermissions'

type Props = {
    meta: EventsMetaProps
}

export const Events = (props: Props) => {
    const { meta } = props
    const { canCreateEvent, canManageVisibility } = useResourcePermissions()
    const [loading, setLoading] = useState(true)
    const [events, setEvents] = useState<Event[]>([])
    const [error, setError] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [eventFilterText, setEventFilterText] = useState('')

    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                const result = await response.json()
                setEvents(result)
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching events')
            } finally {
                setLoading(false)
            }
        }

        fetchEvents()
    }, [])

    const handleCreateEvent = async (eventData: any) => {

        const response = await fetch(`${API_BASE_URL}/event`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(eventData)
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        // Add the new event to the list
        if (result.model) {
            setEvents(prevEvents => [...prevEvents, result.model])
        }
    }

    const prefixPresets = useMemo(() => {
        const prefixCounts = events.reduce<Record<string, number>>((counts, event) => {
            if (!event.name.includes(':')) {
                return counts
            }

            const [prefix] = event.name.split(':')
            const trimmedPrefix = prefix.trim()
            if (!trimmedPrefix) {
                return counts
            }

            const preset = `${trimmedPrefix}:`
            counts[preset] = (counts[preset] || 0) + 1

            return counts
        }, {})

        return Object.entries(prefixCounts)
            .filter(([, count]) => count > 1)
            .map(([prefix]) => prefix)
            .sort((firstPrefix, secondPrefix) => firstPrefix.localeCompare(secondPrefix, undefined, { numeric: true, sensitivity: 'base' }))
    }, [events])

    const filteredEvents = useMemo(() => {
        const normalizedFilterText = eventFilterText.trim().toLowerCase()
        if (!normalizedFilterText) {
            return events
        }

        return events.filter(event => event.name.toLowerCase().includes(normalizedFilterText))
    }, [events, eventFilterText])

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className='mb-4'>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>
                        {canCreateEvent() && (
                            <div className="flex justify-center">
                                <Button onClick={() => setIsModalOpen(true)} size="lg">
                                    <PlusIcon className="w-5 h-5" />
                                    {meta.createButton}
                                </Button>
                            </div>
                        )}
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div
                                className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && events.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noEventsMessage}</p>
                        </div>
                    )}

                    {!loading && !error && events.length > 0 && (
                        <div className="space-y-6">
                            <Card className="border border-gray-200 dark:border-gray-700 p-4">
                                <label htmlFor="event-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {meta.filterLabel}
                                </label>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <input
                                        id="event-filter"
                                        type="text"
                                        value={eventFilterText}
                                        onChange={(event) => setEventFilterText(event.target.value)}
                                        placeholder={meta.filterPlaceholder}
                                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-4 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                    {eventFilterText.trim() && (
                                        <Button type="button" variant="outline" color="neutral" onClick={() => setEventFilterText('')}>
                                            {meta.clearFilterButton}
                                        </Button>
                                    )}
                                </div>
                                {prefixPresets.length > 0 && (
                                    <div className="mt-4 flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {meta.filterPresetsLabel}
                                        </span>
                                        {prefixPresets.map((preset) => (
                                            <Button
                                                key={preset}
                                                type="button"
                                                size="sm"
                                                variant={eventFilterText.trim() === preset ? 'solid' : 'outline'}
                                                color="primary"
                                                onClick={() => setEventFilterText(preset)}
                                            >
                                                {preset}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </Card>

                            {filteredEvents.length === 0 ? (
                                <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                                    <p className="text-xl">{meta.noFilteredEventsMessage}</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filteredEvents.map((event) => (
                                        <EventCard key={event.id} event={event} meta={EventCardMeta} showVisibility={canManageVisibility()} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <EventFormModal
                meta={EventFormModalMeta}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleCreateEvent}
                canManageVisibility={canManageVisibility()}
            />
        </Fragment>
    )
}
